import DwnStash from 'common/stash/DwnStash';
import Environment from 'common/utils/Environment';
import { timeoutIdlePromise } from 'common/utils/timeoutIdlePromise';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { KeycloakInstance } from 'keycloak-js';
import Cookies, { CookieSetOptions } from 'universal-cookie';

const META_KEY = 'kc-meta';
const CUSTOMER_TOKEN_KEY = 'token';
const JUMP_TOKEN_KEY = 'jump-token';
const CUSTOMER_GRANT = 'grant';
const JUMP_GRANT = 'jump-grant';
const cookies = new Cookies();

const clientName = 'aphex-frontend';
const authURL = Environment.REACT_APP_KEYCLOAK_URL + '/auth/';

const cookieAge = 1800;
const cookieConfig: CookieSetOptions = {
  path: '/',
  sameSite: 'lax' as const,
  maxAge: cookieAge,
  secure: true,
};

//keycloak
const getKeycloak = (realm: string): KeycloakInstance => {
  return Keycloak({
    realm: realm,
    url: authURL,
    clientId: clientName,
  });
};

const safeInit = (kc: KeycloakInstance, opt: KeycloakInitOptions) => {
  return timeoutIdlePromise(() => {
    return kc.init(opt);
  }, 8000);
};

//grant storage

export type LocalGrant = { token?: string; idToken?: string; refreshToken?: string; skew?: number };

const deriveGrant = (kc: KeycloakInstance): LocalGrant => {
  return {
    token: kc.token,
    idToken: kc.idToken,
    refreshToken: kc.refreshToken,
    skew: Math.floor(new Date().getTime() / 1000) - kc.tokenParsed!.iat!,
  };
};

function setGrant(key: string, obj: LocalGrant) {
  window.localStorage.setItem(key, JSON.stringify(obj));
}

function getGrant(key: string): LocalGrant {
  const raw = window.localStorage.getItem(key);
  return raw ? JSON.parse(raw) : {};
}

function clearLocalGrant(key: string) {
  window.localStorage.removeItem(key);
}

//meta cookie
function updateStore(obj: any) {
  cookies.set(META_KEY, Object.assign(getStore(), obj, { exp: new Date().getTime() + cookieAge * 1000 }), cookieConfig);
}

function getStore() {
  const raw = cookies.get(META_KEY);
  return raw ? raw : {};
}

function refreshStore() {
  cookies.set(META_KEY, Object.assign(getStore(), { exp: new Date().getTime() + cookieAge * 1000 }), cookieConfig);
}

function clearStore() {
  cookies.remove(META_KEY);
}

//token cookies
//max cookie size is 4096 including meta data, we need to store large token cookies in chunks to get over this limit
const maxChunkSize = 4000;
function _chunkSubstr(str: string, size: number) {
  const numChunks = Math.ceil(str.length / size);
  const chunks = new Array<string>(numChunks);

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str.substr(o, size);
  }

  return chunks;
}

function getTokenCookie(key: string) {
  const strArr: string[] = [];
  let counter = 0;
  let fragment: string;
  while ((fragment = cookies.get(`${key}_${counter++}`))) {
    strArr.push(fragment);
  }
  return strArr.length > 0 ? strArr.join('') : undefined;
}

function setTokenCookie(key: string, token: string) {
  if (!token) {
    throw new Error('Attempted to set an undefined token');
  }
  deleteTokenCookie(key);
  const pieces = _chunkSubstr(token, maxChunkSize);
  for (let i = 0; i < pieces.length; ++i) {
    cookies.set(`${key}_${i}`, pieces[i], cookieConfig);
  }
}

function deleteTokenCookie(key: string) {
  Object.keys(cookies.getAll({ doNotParse: true }))
    .filter((name: string) => name.startsWith(key))
    .forEach((name) => {
      cookies.remove(name);
    });
}

function deleteTokenCookies() {
  deleteTokenCookie(CUSTOMER_TOKEN_KEY);
  deleteTokenCookie(JUMP_TOKEN_KEY);
}

function clearLoginStores() {
  DwnStash.clear();
  clearStore();
  deleteTokenCookies();
  clearLocalGrant(JUMP_GRANT);
  clearLocalGrant(CUSTOMER_GRANT);
  Object.keys(window.localStorage)
    .filter((x) => x.startsWith('pr.'))
    .forEach((x) => localStorage.removeItem(x));
}

const DwnKeycloak = {
  getKeycloak,
  safeInit,
  deriveGrant,
  setGrant,
  getGrant,
  updateStore,
  getStore,
  refreshStore,
  getTokenCookie,
  setTokenCookie,
  clearLoginStores,
};

export default DwnKeycloak;

export { META_KEY, CUSTOMER_TOKEN_KEY, JUMP_TOKEN_KEY, CUSTOMER_GRANT, JUMP_GRANT };
